import { useContext, useEffect, useState, useRef } from 'react';
import {
    ActionDependant,
    dataLocationToPath,
    evaluateTemplateValue,
    GlobalDataContext,
    TemplateContext,
    useEvaluatedAttributes,
} from '@ea-lab/reactive-json';

/**
 * DateTimeWithTimezoneField component - Displays datetime in user timezone but stores UTC
 * 
 * Usage in YAML:
 * - type: DateTimeWithTimezoneField
 *   dataLocation: ~.editable_formData.date_from
 *   timezone: ~.userInfo.timezone  # optional, defaults to user timezone
 *   outputFormat: "iso"  # optional, "iso" (default) or "odoo" (YYYY-MM-DD HH:MM:SS)
 *   forceWrapper: false
 *   inputAttributes:
 *     class: ~~.styling.shared.inputClass.inline
 */
export const DateTimeWithTimezoneField = ({ props, currentData, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);
    
    // Evaluate dataLocation path.
    const dataLocation = props.dataLocation;
    
    // Get output format (default: 'iso' for ISO 8601, 'odoo' for YYYY-MM-DD HH:MM:SS).
    // outputFormat might be a template value, so evaluate it if needed.
    const outputFormatRaw = props.outputFormat;
    const outputFormat = outputFormatRaw 
        ? (typeof outputFormatRaw === 'string' && (outputFormatRaw.startsWith('~') || outputFormatRaw.startsWith('~~'))
            ? evaluateTemplateValue({
                valueToEvaluate: outputFormatRaw,
                globalDataContext,
                templateContext,
            })
            : outputFormatRaw)
        : 'iso';
    
    // Debug log to see what we're getting.
    console.log('[DateTimeWithTimezoneField] Initialized:', {
        outputFormatRaw,
        outputFormat,
        dataLocation,
        props: Object.keys(props),
    });
    
    // Get current UTC value from dataLocation.
    const currentUtcValue = dataLocation 
        ? evaluateTemplateValue({
            valueToEvaluate: dataLocation,
            globalDataContext,
            templateContext,
        })
        : '';
    
    // Get timezone from props or fallback to userInfo.timezone or browser default.
    const timezoneProp = props.timezone 
        ? evaluateTemplateValue({
            valueToEvaluate: props.timezone,
            globalDataContext,
            templateContext,
        })
        : null;
    
    const timezone = timezoneProp || Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // State for display value (in user timezone).
    const [displayValue, setDisplayValue] = useState('');
    const [isInitialized, setIsInitialized] = useState(false);
    const lastUtcValueRef = useRef(null);
    
    // Convert UTC to user timezone for display.
    const utcToLocal = (storedValue) => {
        if (!storedValue) return '';
        
        try {
            // Deserialize stored value to Date object.
            const utcDate = deserializeDate(storedValue);
            if (!utcDate || isNaN(utcDate.getTime())) {
                return '';
            }
            
            // Format for datetime-local input (YYYY-MM-DDTHH:mm).
            const formatter = new Intl.DateTimeFormat('en-CA', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
            
            const parts = formatter.formatToParts(utcDate);
            const year = parts.find(p => p.type === 'year').value;
            const month = parts.find(p => p.type === 'month').value;
            const day = parts.find(p => p.type === 'day').value;
            const hour = parts.find(p => p.type === 'hour').value;
            const minute = parts.find(p => p.type === 'minute').value;
            
            return `${year}-${month}-${day}T${hour}:${minute}`;
        } catch (error) {
            console.error('Error converting UTC to local:', error);
            return '';
        }
    };
    
    // Serialize UTC date to the requested format.
    // Accepts either a Date object or an ISO string.
    const serializeUtcDate = (utcDate) => {
        if (!utcDate) return '';
        
        try {
            // Convert to Date object if it's a string.
            const date = utcDate instanceof Date ? utcDate : new Date(utcDate);
            if (isNaN(date.getTime())) {
                return '';
            }
            
            console.log('[DateTimeWithTimezoneField] serializeUtcDate:', {
                outputFormat,
                input: utcDate,
                date: date.toISOString(),
            });
            
            if (outputFormat === 'odoo') {
                // Format as YYYY-MM-DD HH:MM:SS (Odoo format, UTC).
                const year = date.getUTCFullYear();
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                const hour = String(date.getUTCHours()).padStart(2, '0');
                const minute = String(date.getUTCMinutes()).padStart(2, '0');
                const second = String(date.getUTCSeconds()).padStart(2, '0');
                const formatted = `${year}-${month}-${day} ${hour}:${minute}:${second}`;
                console.log('[DateTimeWithTimezoneField] Formatted as Odoo:', formatted);
                return formatted;
            } else {
                // Default: ISO 8601 format.
                const iso = date.toISOString();
                console.log('[DateTimeWithTimezoneField] Formatted as ISO:', iso);
                return iso;
            }
        } catch (error) {
            console.error('Error serializing UTC date:', error);
            return '';
        }
    };
    
    // Deserialize date from stored format to Date object.
    const deserializeDate = (storedValue) => {
        if (!storedValue) return null;
        
        try {
            if (outputFormat === 'odoo') {
                // Parse YYYY-MM-DD HH:MM:SS format (assumed UTC).
                const dateTimeString = storedValue.replace(' ', 'T') + 'Z';
                return new Date(dateTimeString);
            } else {
                // Default: ISO 8601 format.
                return new Date(storedValue);
            }
        } catch (error) {
            console.error('Error deserializing date:', error);
            return null;
        }
    };
    
    // Convert user timezone to UTC and serialize.
    const localToUtc = (localString) => {
        if (!localString) return '';
        
        try {
            // Parse local datetime string (YYYY-MM-DDTHH:mm).
            // We need to interpret this as a time in the user's timezone and convert to UTC.
            
            // Parse the input string.
            const [datePart, timePart] = localString.split('T');
            const [year, month, day] = datePart.split('-');
            const [hour, minute] = timePart.split(':');
            
            // Create a date string that represents the local time.
            // We'll create it as if it were in UTC, then adjust.
            const dateString = `${year}-${month}-${day}T${hour}:${minute}:00`;
            
            // Create a date object (will be interpreted as browser local time).
            const localDate = new Date(dateString);
            
            // Get what this date/time looks like in the target timezone.
            const formatter = new Intl.DateTimeFormat('en-CA', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            });
            
            const parts = formatter.formatToParts(localDate);
            const tzYear = parts.find(p => p.type === 'year').value;
            const tzMonth = parts.find(p => p.type === 'month').value;
            const tzDay = parts.find(p => p.type === 'day').value;
            const tzHour = parts.find(p => p.type === 'hour').value;
            const tzMinute = parts.find(p => p.type === 'minute').value;
            
            // Calculate the difference between what we want (input) and what we got (tz representation).
            const desiredDate = new Date(`${year}-${month}-${day}T${hour}:${minute}:00`);
            const actualInTz = new Date(`${tzYear}-${tzMonth}-${tzDay}T${tzHour}:${tzMinute}:00`);
            
            // The offset tells us how much to adjust.
            const offsetMs = desiredDate.getTime() - actualInTz.getTime();
            
            // Adjust: subtract the offset to get the correct UTC time.
            const utcTime = localDate.getTime() - offsetMs;
            const utcDate = new Date(utcTime);
            
            // Serialize according to outputFormat (pass Date object directly).
            return serializeUtcDate(utcDate);
        } catch (error) {
            console.error('Error converting local to UTC:', error);
            return '';
        }
    };
    
    // Initialize display value from UTC value.
    useEffect(() => {
        if (currentUtcValue !== lastUtcValueRef.current) {
            lastUtcValueRef.current = currentUtcValue;
            const localValue = utcToLocal(currentUtcValue);
            setDisplayValue(localValue);
            setIsInitialized(true);
        }
    }, [currentUtcValue, timezone]);
    
    // Handle display input change.
    const handleDisplayChange = (e) => {
        const newDisplayValue = e.target.value;
        setDisplayValue(newDisplayValue);
        
        // Convert to UTC and update dataLocation.
        if (dataLocation) {
            const utcValue = localToUtc(newDisplayValue);
            console.log('[DateTimeWithTimezoneField] Converting:', {
                localInput: newDisplayValue,
                outputFormat: outputFormat,
                utcValue: utcValue,
                dataLocation: dataLocation,
            });
            // Convert dataLocation to full path using reactive-json utility.
            const dataPath = dataLocationToPath({
                dataLocation: dataLocation,
                currentPath: path,
                globalDataContext,
                templateContext,
            });
            globalDataContext.updateData(utcValue, dataPath);
        }
    };
    
    // Get input attributes.
    const inputAttributes = props.inputAttributes || {};
    const evaluatedInputAttributes = useEvaluatedAttributes(inputAttributes);
    const inputClass = evaluatedInputAttributes.class || '';
    
    // Don't render until timezone is available.
    if (!timezone || !isInitialized) {
        return null;
    }
    
    return (
        <ActionDependant {...props}>
            <>
                {/* Hidden input with UTC value - this is what gets stored. */}
                <input
                    type="hidden"
                    name={dataLocation || ''}
                    value={currentUtcValue || ''}
                />
                {/* Visible input with timezone-converted value. */}
                <input
                    type="datetime-local"
                    value={displayValue}
                    onChange={handleDisplayChange}
                    className={inputClass}
                    {...evaluatedInputAttributes}
                />
            </>
        </ActionDependant>
    );
};
